// ============================================
// authService.js - Authentication Service
// ============================================
// Handles all auth operations:
// - Login/Register/Logout
// - Token management
// - User data management
// - Device linking on auth

import api, {
  isAuthenticated,
  getStoredUser,
  setStoredUser,
  clearAuth,
  getTokens,
} from './api';
import { linkDeviceToUser } from './chatService';

// ============================================
// AUTH STATE
// ============================================

/**
 * Check if user is currently authenticated
 */
export function checkAuth() {
  return {
    isAuthenticated: isAuthenticated(),
    user: getStoredUser(),
  };
}

/**
 * Get current user from localStorage
 */
export function getCurrentUser() {
  return getStoredUser();
}

// ============================================
// LOGIN
// ============================================

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export async function login(email, password) {
  const result = await api.login(email, password);

  if (result.error) {
    return { success: false, error: result.error };
  }

  // Tokens are stored by api.login()
  // Now fetch user data
  const userResult = await api.getUser();

  if (userResult.error) {
    return { success: false, error: userResult.error };
  }

  // Link device to user (transfer anonymous sessions)
  try {
    await linkDeviceToUser();
  } catch (e) {
    console.warn('Device linking failed:', e);
  }

  return {
    success: true,
    user: userResult.data,
  };
}

// ============================================
// REGISTER
// ============================================

/**
 * Register a new user
 * @param {Object} data - {email, password, password2, username?}
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export async function register(data) {
  const result = await api.register(data);

  if (result.error) {
    // Handle validation errors
    if (result.data && typeof result.data === 'object') {
      // Django returns field-specific errors
      const errors = Object.entries(result.data)
        .map(([field, msgs]) => {
          const message = Array.isArray(msgs) ? msgs[0] : msgs;
          return `${field}: ${message}`;
        })
        .join(', ');
      return { success: false, error: errors };
    }
    return { success: false, error: result.error };
  }

  // Tokens and user are stored by api.register()

  // Link device to user (transfer anonymous sessions)
  try {
    await linkDeviceToUser();
  } catch (e) {
    console.warn('Device linking failed:', e);
  }

  return {
    success: true,
    user: result.data.user,
  };
}

// ============================================
// LOGOUT
// ============================================

/**
 * Logout current user
 */
export function logout() {
  clearAuth();
  // Note: We keep the fingerprint so device can still be tracked
}

// ============================================
// USER PROFILE
// ============================================

/**
 * Fetch fresh user data from backend
 * @returns {Promise<{user?: Object, error?: string}>}
 */
export async function fetchUser() {
  if (!isAuthenticated()) {
    return { user: null };
  }

  const result = await api.getUser();

  if (result.error) {
    // If 401, token is invalid
    if (result.status === 401) {
      clearAuth();
    }
    return { error: result.error };
  }

  return { user: result.data };
}

/**
 * Update user profile
 * @param {Object} updates - {username?, phone?}
 */
export async function updateProfile(updates) {
  const result = await api.updateUser(updates);

  if (result.error) {
    return { success: false, error: result.error };
  }

  // Update stored user
  setStoredUser(result.data);

  return { success: true, user: result.data };
}

/**
 * Change password
 * @param {string} oldPassword
 * @param {string} newPassword
 */
export async function changePassword(oldPassword, newPassword) {
  const result = await api.changePassword(oldPassword, newPassword);

  if (result.error) {
    return { success: false, error: result.error };
  }

  return { success: true };
}

// ============================================
// USAGE
// ============================================

/**
 * Get authenticated user's usage stats
 */
export async function getUsageStats() {
  if (!isAuthenticated()) {
    return { error: 'Not authenticated' };
  }

  const result = await api.getAccountUsage();

  if (result.error) {
    return { error: result.error };
  }

  return {
    usage: {
      tier: result.data.tier,
      essaysToday: result.data.essays_today,
      essaysTotal: result.data.essays_total,
      remainingToday: result.data.remaining_today,
      dailyLimit: result.data.daily_limit,
      subscriptionActive: result.data.subscription_active,
    },
  };
}

// ============================================
// TOKEN UTILITIES
// ============================================

/**
 * Get current access token (for external use if needed)
 */
export function getAccessToken() {
  return getTokens().access;
}

/**
 * Check if tokens exist and are potentially valid
 */
export function hasValidTokens() {
  const { access, refresh } = getTokens();
  return !!(access && refresh);
}

// ============================================
// EXPORTS
// ============================================

export default {
  checkAuth,
  getCurrentUser,
  login,
  register,
  logout,
  fetchUser,
  updateProfile,
  changePassword,
  getUsageStats,
  getAccessToken,
  hasValidTokens,
};
